// Core
import React, { useMemo } from 'react';

// Libraries
import _ from 'lodash';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';

// Plugins
import {
  convertTemperature,
  DASHBOARD_NULLISH_TEXT,
  formatNumber,
  roundNumber,
} from '../../../../../../plugins/general';
import {
  DEVICE_COMMUNICATION_THRESHOLD,
  getOperationalAlert,
  isEnabled,
  isEquipmentRunning,
  overallStatusToTagType,
  overallStatusToText,
  permissiveStatusToAlarmIndicatorStatus,
  permissiveStatusToIndicatorStatus,
  vibrationSensorToTagType,
  vibrationSensorToText,
} from '../../../../../../plugins/crusher';
import {
  MOTOR_RPM_RANGE,
  MOTOR_AMPS_RANGE,
  TEMPERATURE_RANGE,
} from '../../../../../../plugins/thresholds';

// Store
import {
  getMeasurementUnit,
} from '../../../../../../store/measurement.store';

// Hooks
import { useBreakpoint } from '../../../../../../hooks/useBreakpoint';

// Types
import {
  MeasurementType,
  TemperatureType,
} from '../../../../../../types/global';
import { Theme } from '../../../../../../types/theme';

// Components
import { AlertType } from '../../../../../../layout/elements/Alert';
import { Button, ButtonSize, ButtonType } from '../../../../../../layout/elements/Button';
import { Col } from '../../../../../../layout/elements/Col';
import { Drawer, DrawerSize } from '../../../../../../layout/elements/Drawer';
import {
  Paragraph,
  ParagraphSize,
  ParagraphWeight,
} from '../../../../../../layout/elements/Paragraph';
import { Row } from '../../../../../../layout/elements/Row';
import { Speedometer } from '../../../../../../layout/elements/Speedometer';
import {
  StatusIndicator,
  StatusIndicatorSize,
} from '../../../../../../layout/elements/StatusIndicator';
import { Tag } from '../../../../../../layout/elements/Tag';
import { Thermometer } from '../../../../../../layout/elements/Thermometer';
import { AlertLine } from '../../../../../../layout/icons';

// Component
import { MotorStatusProps } from './MotorStatus.types';
import {
  CrusherStartListDescription,
  FooterContainer,
  MotorCurrentSpeedometer,
  OverallContainer,
  OverallPermissiveContainer,
  OverallStatusContainer,
  RPMMotorSpeedometer,
  ThermometersContainer,
  VibrationSensorItem,
  VibrationSensorsContainer,
  VibrationSensorsWrapper,
  VibrationTitleContainer,
} from './MotorStatus.style';

function MotorStatus({
  className,
  edgeStatus,
  isDrawerOpen,
  onCloseDrawer,
  onOpenCoastDownHistory,
  testId,
  unitData,
}: MotorStatusProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();
  const theme: Theme = useTheme();
  const { isXs } = useBreakpoint();

  const userTemperatureUnit: TemperatureType = getMeasurementUnit(MeasurementType.temperature);

  const alertText: string | null = getOperationalAlert(
    edgeStatus?.edgeStatus,
    unitData?.timestamp,
    unitData?.readStatus,
    DEVICE_COMMUNICATION_THRESHOLD,
  );

  const renderSensorItem = (
    name: string,
    value: number | null,
    thresholds?: number | null,
  ): JSX.Element => (
    <VibrationSensorItem data-testid={`${testId}-ring-vibration-sensor-item`}>
      <Row>
        <Col cols={11}>
          <Paragraph
            size={ParagraphSize.sm}
            weight={ParagraphWeight.medium}
            color={theme.color.crusherDashboard.motorStatus.sensorListTextColor}
          >
            {`${t('common.sensor')} ${name.replace('vibrationSensor', '')}`}
          </Paragraph>
        </Col>
        <Col cols={9}>
          <Paragraph
            size={ParagraphSize.sm}
            weight={ParagraphWeight.medium}
            color={theme.color.crusherDashboard.motorStatus.sensorListValuePercentageTextColor}
          >
            {_.isNil(value) ? DASHBOARD_NULLISH_TEXT : `${formatNumber(value)}%`}
          </Paragraph>
        </Col>
        <Col cols={4}>
          <Tag
            text={vibrationSensorToText(value, { highWarning: thresholds })}
            type={
              vibrationSensorToTagType(
                value,
                { highWarning: thresholds },
                edgeStatus ? edgeStatus.edgeStatus : null,
                unitData?.timestamp,
              )
            }
          />
        </Col>
      </Row>
    </VibrationSensorItem>
  );

  const renderPermissiveItem = (
    permissiveName: string,
    permissiveValue: boolean | null | undefined,
  ): JSX.Element => (
    <Col xs={24} xl={12}>
      <CrusherStartListDescription
        status={
          permissiveStatusToIndicatorStatus(
            permissiveValue,
            edgeStatus ? edgeStatus.edgeStatus : null,
            unitData?.timestamp,
          )
        }
      >
        <StatusIndicator
          className="list-text-description"
          text={t(`view.crusher.motorStatus.drawer.${permissiveName}`)}
          status={
            permissiveStatusToIndicatorStatus(
              permissiveValue,
              edgeStatus ? edgeStatus.edgeStatus : null,
              unitData?.timestamp,
            )
          }
        />
      </CrusherStartListDescription>
    </Col>
  );

  // Inverted Permissive Item (false = red, true = green)
  const renderInvertedPermissiveItem = (
    permissiveName: string,
    permissiveValue: boolean | null | undefined,
  ): JSX.Element => (
    <Col xs={24} xl={12}>
      <CrusherStartListDescription
        status={
          permissiveStatusToAlarmIndicatorStatus(
            permissiveValue,
            edgeStatus ? edgeStatus.edgeStatus : null,
            unitData?.timestamp,
          )
        }
      >
        <StatusIndicator
          testId={`${testId}-permissive-indicator`}
          className="list-text-description"
          text={t(`view.crusher.motorStatus.drawer.${permissiveName}`)}
          status={
            permissiveStatusToAlarmIndicatorStatus(
              permissiveValue,
              edgeStatus ? edgeStatus.edgeStatus : null,
              unitData?.timestamp,
            )
          }
        />
      </CrusherStartListDescription>
    </Col>
  );

  const colSizeCountershaftBushingTemp = useMemo((): number => {
    let size: number = 24;
    if (!isEnabled(unitData?.counterShaftBushingTempEnable)) {
      size = 0;
    } else if (isEnabled(unitData?.eccentricBushingTempEnable)) {
      size = 12;
    }
    return size;
  }, [unitData?.counterShaftBushingTempEnable, unitData?.eccentricBushingTempEnable]);

  const colSizeCountershaftEccentricTemp = useMemo((): number => {
    let size: number = 24;
    if (!isEnabled(unitData?.eccentricBushingTempEnable)) {
      size = 0;
    } else if (isEnabled(unitData?.counterShaftBushingTempEnable)) {
      size = 12;
    }
    return size;
  }, [unitData?.counterShaftBushingTempEnable, unitData?.eccentricBushingTempEnable]);

  const hasVibrationSensorsEnabled: boolean = useMemo(() => (
    isEnabled(unitData?.vibrationSensor1Enable)
    || isEnabled(unitData?.vibrationSensor2Enable)
    || isEnabled(unitData?.vibrationSensor3Enable)
    || isEnabled(unitData?.vibrationSensor4Enable)
  ), [
    unitData?.vibrationSensor1Enable,
    unitData?.vibrationSensor2Enable,
    unitData?.vibrationSensor3Enable,
    unitData?.vibrationSensor4Enable,
  ]);

  // Check if at least one temperature sensor is enabled
  const hasTemperatureSensorsEnabled: boolean = useMemo(() => (
    isEnabled(unitData?.counterShaftBushingTempEnable)
    || isEnabled(unitData?.eccentricBushingTempEnable)
  ), [
    unitData?.counterShaftBushingTempEnable,
    unitData?.eccentricBushingTempEnable,
  ]);

  const colSizeVibrationSensors = useMemo((): number => {
    let size: number = 24;
    if (!hasVibrationSensorsEnabled) {
      size = 0;
    } else if (hasTemperatureSensorsEnabled) {
      size = 12;
    }
    return size;
  }, [
    hasVibrationSensorsEnabled,
    hasTemperatureSensorsEnabled,
  ]);

  const colSizeThermometers = useMemo((): number => {
    let size: number = 24;
    if (!hasTemperatureSensorsEnabled) {
      size = 0;
    } else if (hasVibrationSensorsEnabled) {
      size = 12;
    }
    return size;
  }, [hasTemperatureSensorsEnabled, hasVibrationSensorsEnabled]);

  return (
    <Drawer
      testId={testId}
      className={className}
      title={t('view.crusher.motorStatus.drawer.title')}
      open={isDrawerOpen}
      onClose={onCloseDrawer}
      footer={false}
      alert={
        alertText
          ? {
            type: AlertType.warning,
            icon: <AlertLine />,
            description: alertText,
          }
          : false
      }
      size={DrawerSize.lg}
    >
      <OverallContainer>
        <Row>
          <Col>
            <OverallStatusContainer data-testid={`${testId}-overall-status-container`}>
              <Paragraph
                weight={ParagraphWeight.bold}
                size={isXs ? ParagraphSize.sm : ParagraphSize.md}
                testId={`${testId}-overall-status-description`}
              >
                {t('view.crusher.overallStatus')}
              </Paragraph>
              <Tag
                text={overallStatusToText(unitData?.crusherMotorOverallStatus)}
                type={
                  overallStatusToTagType(
                    unitData?.crusherMotorOverallStatus,
                    edgeStatus ? edgeStatus.edgeStatus : null,
                    unitData?.timestamp,
                  )
                }
              />
            </OverallStatusContainer>
          </Col>
        </Row>

        {/* Motor RPM and Motor Amps */}
        <Row>
          <Col
            cols={isEnabled(unitData?.crusherSpeedMonitorEnable) ? 12 : 0}
            testId="motor-rpm-speedometer-col"
          >
            <RPMMotorSpeedometer data-testid={`${testId}-motor-rpm-speedometer-container`}>
              <Speedometer
                running={
                  isEquipmentRunning(
                    edgeStatus,
                    unitData?.timestamp,
                    DEVICE_COMMUNICATION_THRESHOLD,
                  )
                }
                title={`${t('view.crusher.motorStatus.drawer.motorRpm')}`}
                minValue={
                  _.isNil(unitData?.crusherMotorRpm)
                    ? null
                    : MOTOR_RPM_RANGE.minValue
                }
                maxValue={
                  _.isNil(unitData?.crusherMotorRpm)
                    ? null
                    : MOTOR_RPM_RANGE.maxValue
                }
                value={unitData?.crusherMotorRpm}
                unit="rpm"
                thresholds={{
                  lowWarning: unitData?.spBeltSlipAlarmRpm,
                }}
              />
            </RPMMotorSpeedometer>
          </Col>

          <Col
            cols={isEnabled(unitData?.crusherSpeedMonitorEnable) ? 12 : 24}
            testId="motor-amps-speedometer-col"
          >
            <MotorCurrentSpeedometer data-testid={`${testId}-motor-amps-speedometer-container`}>
              <Speedometer
                running={
                  isEquipmentRunning(
                    edgeStatus,
                    unitData?.timestamp,
                    DEVICE_COMMUNICATION_THRESHOLD,
                  )
                }
                title={`${t('view.crusher.motorStatus.drawer.motorAmps')}`}
                minValue={
                  _.isNil(unitData?.crusherMotorAmps)
                    ? null
                    : MOTOR_AMPS_RANGE.minValue
                }
                maxValue={
                  _.isNil(unitData?.crusherMotorAmps)
                    ? null
                    : MOTOR_AMPS_RANGE.maxValue
                }
                value={unitData?.crusherMotorAmps}
                unit="A"
                thresholds={{
                  highWarning: unitData?.spCrusherMotorFullLoadAmps,
                }}
              />
            </MotorCurrentSpeedometer>
          </Col>
        </Row>

        {/* Thermometers and sensors */}
        <Row>
          <Col
            xs={hasTemperatureSensorsEnabled ? 24 : 0}
            xl={colSizeThermometers}
          >
            <ThermometersContainer
              data-testid={`${testId}-thermometers-container`}
              hasVibrationSensorsEnabled={hasVibrationSensorsEnabled}
            >
              <Row>
                <Col
                  cols={colSizeCountershaftBushingTemp}
                  testId="countershaft-bushing-thermometer-col"
                >
                  <Thermometer
                    running={
                      isEquipmentRunning(
                        edgeStatus,
                        unitData?.timestamp,
                        DEVICE_COMMUNICATION_THRESHOLD,
                      )
                    }
                    minValue={
                      _.isNil(unitData?.countershaftBushingTemp)
                        ? null
                        : TEMPERATURE_RANGE(userTemperatureUnit).minValue
                    }
                    maxValue={
                      _.isNil(unitData?.countershaftBushingTemp)
                        ? null
                        : TEMPERATURE_RANGE(userTemperatureUnit).maxValue
                    }
                    value={convertTemperature(
                      unitData?.countershaftBushingTemp,
                      userTemperatureUnit,
                      1,
                    ).value}
                    unit={userTemperatureUnit}
                    title={`${t('view.crusher.motorStatus.drawer.countershaftBushingTemp')}`}
                  />
                </Col>
                <Col
                  cols={colSizeCountershaftEccentricTemp}
                  testId="eccentric-bushing-thermometer-col"
                >
                  <Thermometer
                    running={
                      isEquipmentRunning(
                        edgeStatus,
                        unitData?.timestamp,
                        DEVICE_COMMUNICATION_THRESHOLD,
                      )
                    }
                    minValue={
                      _.isNil(unitData?.countershaftEccentricTemp)
                        ? null
                        : TEMPERATURE_RANGE(userTemperatureUnit).minValue
                    }
                    maxValue={
                      _.isNil(unitData?.countershaftEccentricTemp)
                        ? null
                        : TEMPERATURE_RANGE(userTemperatureUnit).maxValue
                    }
                    value={convertTemperature(
                      unitData?.countershaftEccentricTemp,
                      userTemperatureUnit,
                      1,
                      false,
                      false,
                    ).value}
                    unit={userTemperatureUnit}
                    thresholds={{
                      highAlarm: convertTemperature(
                        unitData?.spEccentricBushingTempHighStopFeed,
                        userTemperatureUnit,
                        1,
                        false,
                        false,
                      ).value,
                    }}
                    title={`${t('view.crusher.motorStatus.drawer.countershaftEccentricTemp')}`}
                  />
                </Col>
              </Row>
            </ThermometersContainer>
          </Col>
          <VibrationSensorsContainer
            xs={hasVibrationSensorsEnabled ? 24 : 0}
            xl={colSizeVibrationSensors}
          >
            <VibrationSensorsWrapper data-testid={`${testId}-ring-vibration-container`}>
              <VibrationTitleContainer>
                <Paragraph
                  weight={ParagraphWeight.bold}
                  size={isXs ? ParagraphSize.sm : ParagraphSize.md}
                  testId={`${testId}-ring-vibration-title`}
                >
                  {t('view.crusher.motorStatus.drawer.ringVibration')}
                </Paragraph>
              </VibrationTitleContainer>
              {isEnabled(unitData?.vibrationSensor1Enable) && renderSensorItem(
                'vibrationSensor1',
                roundNumber(unitData?.vibrationSensor1),
                unitData?.spVibration1Warning,
              )}
              {isEnabled(unitData?.vibrationSensor2Enable) && renderSensorItem(
                'vibrationSensor2',
                roundNumber(unitData?.vibrationSensor2),
                unitData?.spVibration2Warning,
              )}
              {isEnabled(unitData?.vibrationSensor3Enable) && renderSensorItem(
                'vibrationSensor3',
                roundNumber(unitData?.vibrationSensor3),
                unitData?.spVibration3Warning,
              )}
              {isEnabled(unitData?.vibrationSensor4Enable) && renderSensorItem(
                'vibrationSensor4',
                roundNumber(unitData?.vibrationSensor4),
                unitData?.spVibration4Warning,
              )}
            </VibrationSensorsWrapper>
          </VibrationSensorsContainer>
        </Row>

        {/* Permissives */}
        <Row>
          <Col>
            <OverallPermissiveContainer>
              <StatusIndicator
                text={t('view.crusher.motorStatus.drawer.crusherStart')}
                status={
                  permissiveStatusToIndicatorStatus(
                    unitData?.crusherStartPermissives,
                    edgeStatus ? edgeStatus.edgeStatus : null,
                    unitData?.timestamp,
                  )
                }
                size={StatusIndicatorSize.lg}
                testId={`${testId}-crusher-start-permissive-indicator`}
              />
            </OverallPermissiveContainer>
            <Row style={{ paddingBottom: 24 }} gutter={50}>
              {/* This tag has inverted logic of true and false (true = green, false = red) */}
              {renderInvertedPermissiveItem('calibrationActive', unitData?.calibrationActive)}
              {renderPermissiveItem(
                'lubePressureTransducerFault',
                unitData?.lubePressureTransducerFault,
              )}
              {renderPermissiveItem('lubeOilLevelLow', unitData?.lubeOilLevelLow)}
              {renderPermissiveItem('lubeReturnLowTemp', unitData?.lubeReturnLowTemp)}
              {renderPermissiveItem('crusherBeltSlip', unitData?.crusherBeltSlip)}
              {renderPermissiveItem('crusherAboveMinimumCSS', unitData?.crusherAboveMinimumCSS)}
              {unitData?.crusherMotorQty && renderPermissiveItem(
                'crusherMotor2Fault',
                unitData?.crusherMotor2Fault,
              )}
              {renderPermissiveItem('lowLubeOilPressure', unitData?.lowLubeOilPressure)}
              {renderPermissiveItem('crusherMotor1Fault', unitData?.crusherMotor1Fault)}
              {renderPermissiveItem('minimumLubeRunTimer', unitData?.minimumLubeRunTimer)}
              {renderPermissiveItem('lubeReturnTempHigh', unitData?.lubeReturnTempHigh)}
              {renderPermissiveItem('lowLubeOil', unitData?.lowLubeOil)}
              {/* This tag has inverted logic of true and false (true = green, false = red) */}
              {isEnabled(unitData?.counterShaftBushingTempEnable) && renderInvertedPermissiveItem(
                'countershaftBushingTransducerFault',
                unitData?.countershaftBushingTransducerFault,
              )}
              {renderPermissiveItem(
                'lubeReturnTempTransducerFault',
                unitData?.lubeReturnTempTransducerFault,
              )}
              {isEnabled(unitData?.cavityLevelSensorEnable) && renderPermissiveItem(
                'cavityLevelTransducerFault',
                unitData?.cavityLevelTransducerFault,
              )}
            </Row>
          </Col>
        </Row>
      </OverallContainer>

      {/* Coast Down History */}
      <Row>
        <FooterContainer data-testid={`${testId}-coast-down-history-footer-container`}>
          <Paragraph
            weight={ParagraphWeight.bold}
            size={isXs ? ParagraphSize.sm : ParagraphSize.md}
            testId={`${testId}-coast-down-history-footer-description`}
          >
            {t('view.crusher.motorStatus.drawer.coastDownHistory')}
          </Paragraph>
          <Button
            type={ButtonType.secondary}
            block={false}
            onClick={onOpenCoastDownHistory}
            size={isXs ? ButtonSize.xs : ButtonSize.md}
            testId={`${testId}-coast-down-history-footer-button`}
          >
            {t('common.openChart')}
          </Button>
        </FooterContainer>
      </Row>
    </Drawer>
  );
}

export { MotorStatus };
